import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/db64e9kbe24agvuu3fvg';
const enquiryTypes = ['Talent Acquisition', 'HR Advisory', 'People & Organisation', 'Career Enquiry', 'Other'];

type Errors = Partial<Record<'name' | 'email' | 'enquiry_type' | 'message', string>>;

const fieldCls = 'w-full bg-transparent border-0 border-b border-foreground-950/30 hover:border-foreground-950/55 focus:border-foreground-950 focus-visible:border-b-2 outline-none px-0 py-3 text-[15px] text-foreground-950 transition-colors duration-200 ease-ui';
const labelCls = 'block font-label text-[12px] tracking-[0.12em] uppercase text-foreground-600 mb-1';

export default function ContactForm() {
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [msgLen, setMsgLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honey = String(fd.get('company_alt') || '').trim();
    if (honey) { setStatus('success'); return; }
    fd.delete('company_alt');

    const next: Errors = {};
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const type = String(fd.get('enquiry_type') || '');
    const message = String(fd.get('message') || '').trim();
    if (!name) next.name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid work email.';
    if (!type) next.enquiry_type = 'Please choose an enquiry type.';
    if (!message) next.message = 'Please tell us a little about your enquiry.';
    else if (message.length > 500) next.message = 'Please keep your message under 500 characters.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, String(v)));
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
      const serverMsg = parsed?.meta?.message || parsed?.message || detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('We could not send your enquiry. Please check your connection and try again.');
    }
  };

  if (status === 'success') {
    return (
      <div role="status" className="bg-background-100 p-8 md:p-12">
        <span className="w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 text-xl"><i className="ri-check-line"></i></span>
        <h2 className="mt-6 font-heading text-[32px] text-foreground-950">Thank you — your enquiry has been sent.</h2>
        <p className="mt-3 text-[15px] text-foreground-700">We&apos;ll review your message and be in touch.</p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-6 text-[14px] underline cursor-pointer whitespace-nowrap">Send another enquiry</button>
      </div>
    );
  }

  return (
    <form id="contact-enquiry" data-readdy-form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8 relative">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <label htmlFor="cf-name" className={labelCls}>Full Name *</label>
          <input id="cf-name" name="name" type="text" autoComplete="name" aria-invalid={!!errors.name} aria-describedby="cf-name-err" className={fieldCls} />
          {errors.name && <p id="cf-name-err" className="mt-1.5 text-[13px] text-accent-700">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>Work Email *</label>
          <input id="cf-email" name="email" type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby="cf-email-err" className={fieldCls} />
          {errors.email && <p id="cf-email-err" className="mt-1.5 text-[13px] text-accent-700">{errors.email}</p>}
        </div>
      </div>
      <div>
        <label htmlFor="cf-company" className={labelCls}>Company / Organisation <span className="text-foreground-500 normal-case tracking-normal">(optional)</span></label>
        <input id="cf-company" name="company" type="text" autoComplete="organization" className={fieldCls} />
      </div>
      <input type="text" name="company_alt" className="form-aux-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly onFocus={(ev) => ev.currentTarget.removeAttribute('readonly')} />
      <fieldset>
        <legend className={`${labelCls} mb-3`}>Enquiry Type *</legend>
        <div className="flex flex-wrap gap-2">
          {enquiryTypes.map((t) => (
            <label key={t} className="cursor-pointer">
              <input type="radio" name="enquiry_type" value={t} className="peer sr-only" />
              <span className="inline-block whitespace-nowrap px-3.5 py-2 text-[13px] border border-foreground-950/25 bg-white text-foreground-800 hover:border-foreground-950/60 peer-checked:bg-primary-500 peer-checked:text-background-50 peer-checked:border-primary-500 peer-focus-visible:ring-2 peer-focus-visible:ring-accent-400 transition-colors">{t}</span>
            </label>
          ))}
        </div>
        {errors.enquiry_type && <p className="mt-1.5 text-[13px] text-accent-700">{errors.enquiry_type}</p>}
      </fieldset>
      <div>
        <label htmlFor="cf-message" className={labelCls}>Message *</label>
        <textarea id="cf-message" name="message" rows={4} maxLength={500} onChange={(ev) => setMsgLen(ev.target.value.length)} aria-invalid={!!errors.message} className={`${fieldCls} resize-none`}></textarea>
        <div className="mt-1.5 flex justify-between text-[13px]">
          <span className="text-accent-700">{errors.message}</span>
          <span className="text-foreground-500">{msgLen}/500</span>
        </div>
      </div>
      {status === 'error' && <p role="alert" className="text-[14px] text-accent-700 bg-accent-50 border border-accent-200 px-4 py-3">{formError}</p>}
      <button type="submit" disabled={status === 'sending'} className="group self-start inline-flex items-center gap-2 bg-primary-500 text-background-50 hover:bg-accent-500 active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 disabled:opacity-60 disabled:cursor-wait disabled:hover:bg-primary-500 transition-[background-color,transform] duration-[240ms] ease-ui px-6 py-3.5 font-label text-[15px] font-medium whitespace-nowrap cursor-pointer">
        {status === 'sending' ? 'Sending…' : 'Send Enquiry'} <i className="ri-arrow-right-up-line arrow-nudge"></i>
      </button>
    </form>
  );
}